/**
 * Handoff técnico — spec viva de la integración para el equipo de desarrollo.
 *
 * Documenta: stack, contratos zod (source of truth para Eze), patrones,
 * decisiones, deuda regulatoria GAMP 5 / 21 CFR Part 11 (14 items).
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface AlcanceItem {
  feature: string;
  v1: 'incluido' | 'parcial' | 'excluido';
  v2: 'incluido' | 'excluido';
  notas: string;
}

const ALCANCE: AlcanceItem[] = [
  {
    feature: 'Auth + sesión (login, logout, multi-GLN con reauth)',
    v1: 'incluido',
    v2: 'incluido',
    notas: 'Backend: integrar con IdP corporativo (Google Workspace / Azure AD). Endpoints listos como zod schemas.',
  },
  {
    feature: 'Despacho (pickeo manual + trazado directo v9)',
    v1: 'incluido',
    v2: 'incluido',
    notas: 'Backend: integrar con ERP Plataforma (ACSIS) para listar remitos pendientes y cerrar tareas.',
  },
  {
    feature: 'Recepción (modo directa + verificación por escaneo)',
    v1: 'incluido',
    v2: 'incluido',
    notas: 'Backend: consumir transferencias entrantes via webhook ANMAT / polling.',
  },
  {
    feature: 'Series + Trazabilidad timeline',
    v1: 'incluido',
    v2: 'incluido',
    notas: 'Lectura de series + sus movimientos. La timeline se reconstruye desde operaciones + movimientos del DB.',
  },
  {
    feature: 'Cola ANMAT (reintentar / editar / cancelar)',
    v1: 'incluido',
    v2: 'incluido',
    notas: 'Backend: gestión de reintentos asincrónicos contra el SNT con retry policy + DLQ.',
  },
  {
    feature: 'Audit Trail + Logs API',
    v1: 'incluido',
    v2: 'incluido',
    notas: 'Backend: hash chain HMAC-SHA256 server-side + replicación. Ver deudas regulatorias.',
  },
  {
    feature: 'Seguridad (usuarios, roles, sesiones, política de timeout)',
    v1: 'incluido',
    v2: 'incluido',
    notas: 'Backend: timeout server-side enforced, no solo cliente. Lockout tras N intentos fallidos.',
  },
  {
    feature: 'DataMatrix GS1 + PDF de trazabilidad',
    v1: 'incluido',
    v2: 'incluido',
    notas: 'Generación cliente-side con bwip-js + jsPDF. No requiere backend para v1.',
  },
  {
    feature: 'Maestros (productos, clientes, eventos ANMAT con defaults custom)',
    v1: 'parcial',
    v2: 'incluido',
    notas: 'v1: read-only desde mock. v2: CRUD completo con audit + invalidation server-side.',
  },
  {
    feature: 'Ingreso / Alta manual de series',
    v1: 'excluido',
    v2: 'incluido',
    notas: 'v1: UI funciona contra mock (validar UX y flujo de etiquetas GS1) pero el backend NO se implementa. Para v1, carga inicial vía script de migración directo a DB. v2: endpoint real + persistencia + audit.',
  },
  {
    feature: 'Lotes (visor consolidado de altas manuales)',
    v1: 'excluido',
    v2: 'incluido',
    notas: 'v1: UI funciona contra mock para demo. v2: depende de Ingreso/Alta. Acciones (re-imprimir etiquetas, eliminar lote) requieren endpoints reales.',
  },
  {
    feature: 'Notificaciones in-app (bell)',
    v1: 'parcial',
    v2: 'incluido',
    notas: 'v1: cliente-side, ad-hoc desde mutations. v2: stream server-side (SSE / WebSocket) + persistencia + lectura cross-device.',
  },
  {
    feature: 'Dashboard con KPIs operativos',
    v1: 'incluido',
    v2: 'incluido',
    notas: 'KPIs derivados en runtime — funciona sin endpoint dedicado. v2 puede agregar endpoint /dashboard/kpis para optimizar consultas pesadas.',
  },
];

interface DeudaItem {
  titulo: string;
  norma: string;
  mockup: string;
  produccion: string;
}

const DEUDA: DeudaItem[] = [
  {
    titulo: 'Hash chain del audit trail no criptográfico',
    norma: '21 CFR Part 11 § 11.70(i) · GAMP 5 cat. 5',
    mockup: 'FNV-1a 32-bit. Detecta tampering manual del localStorage pero un atacante puede recalcular toda la cadena.',
    produccion: 'HMAC-SHA256 con clave server-side. Verificar integridad en server al exportar el audit.',
  },
  {
    titulo: 'Firma electrónica no revalida contraseña',
    norma: '21 CFR Part 11 § 11.100(b) · 11.200',
    mockup: 'Mock acepta cualquier password. FIRMA_ELECTRONICA_ENABLED=false deshabilita el modal completamente. PIN por usuario ya preparado en el modelo (Usuario.pin, paquete AC) — todavía no usado por el modal.',
    produccion: 'Cuando se active el PIN: cambiar FirmaElectronicaModal para que pida `usuario.pin` (4-6 dígitos) en vez del password completo. El PIN se hashea server-side (bcrypt/argon2) y se valida en reautenticar(). Incluso si el login fue por Google SSO, las operaciones GxP críticas requieren PIN local (Part 11 §11.200 "two distinct components"). Admin puede editar el PIN de cada usuario desde Seguridad → Usuarios (UI todavía no implementada — agregar editor inline como el GLN default del paquete W).',
  },
  {
    titulo: 'Timestamps generados client-side',
    norma: '21 CFR Part 11 § 11.70(b) · ALCOA+ "Contemporaneous"',
    mockup: 'new Date().toISOString() en cada entrada. Un operador puede mover el reloj de su máquina.',
    produccion: 'Servidor devuelve ts autoritativo en cada API call. Firma servidor incluida en la entrada del audit.',
  },
  {
    titulo: 'Sin backup ni replicación del audit trail',
    norma: '21 CFR Part 11 § 11.10(e) · retención regulatoria',
    mockup: 'localStorage del browser. Cualquier "Clear data" o cambio de dispositivo borra todo.',
    produccion: 'Base de datos relacional/append-only con replicación geográfica. Retención mínima 10 años.',
  },
  {
    titulo: '"Resetear demo" borra el audit trail completo',
    norma: '21 CFR Part 11 § 11.10(c) · § 11.10(e)',
    mockup: 'En el mockup vanilla había botón "Resetear demo" que destruía data.auditTrail. En React no se incluyó.',
    produccion: 'Ningún UI puede borrar el audit. Reset solo vía admin DB con auditoría externa.',
  },
  {
    titulo: 'Schema migration destructiva',
    norma: 'GAMP 5 — control de cambios sobre datos GxP',
    mockup: 'Cualquier bump de meta.version dispara reset destructivo del estado completo.',
    produccion: 'Scripts de migración up/down por versión que preserven datos. Pruebas en staging antes de cada release.',
  },
  {
    titulo: 'localStorage como única persistencia',
    norma: 'GAMP 5 cat. 5 — arquitectura',
    mockup: 'Single-user, single-tab, sin sincronización multi-tab.',
    produccion: 'Backend con base de datos central, sesiones server-side, RBAC validado en server.',
  },
  {
    titulo: 'Cap LRU a 500 entradas en API Log',
    norma: 'Auditoría regulatoria de comunicaciones externas',
    mockup: 'Las 500 llamadas más recientes. Histórico anterior se pierde silenciosamente.',
    produccion: 'Sin cap o cap mucho mayor (50k+) con archive por fecha. Export periódico a S3/blob storage.',
  },
  {
    titulo: 'RBAC validado solo client-side',
    norma: 'GAMP 5 — defense in depth',
    mockup: 'Hay guards defensivos en funciones, pero cualquier cliente puede modificar localStorage directamente.',
    produccion: 'RBAC enforced server-side en cada endpoint. El client-side es solo UX.',
  },
  {
    titulo: 'Sin timeout de sesión inactiva',
    norma: '21 CFR Part 11 § 11.10(d) · § 11.300(d)',
    mockup: 'Sesión persistida en localStorage sin TTL.',
    produccion: 'Timeout server-side configurable (15-30 min). Re-autenticación para acciones GxP críticas (step-up auth).',
  },
  {
    titulo: 'Sin lockout tras N intentos fallidos',
    norma: '21 CFR Part 11 § 11.300(b) · NIST SP 800-63B',
    mockup: 'login() acepta intentos infinitos. Solo se cuentan en audit pero no bloquean.',
    produccion: 'Lockout tras 5 intentos (15 min) + lockout permanente tras 10. Notificación al titular. Captcha tras 3.',
  },
  {
    titulo: 'Logout no garantizado al cerrar pestaña',
    norma: '21 CFR Part 11 § 11.10(d) · ALCOA+ "Attributable"',
    mockup: 'Cerrar la pestaña deja data.session colgado. Al recargar el usuario queda logueado.',
    produccion: 'Sesiones server-side con TTL (cookies HttpOnly + Secure). Heartbeat client-side. Auto-logout al beforeunload.',
  },
  {
    titulo: 'Sin URS/FS/DS versionados ni rastreabilidad',
    norma: 'GAMP 5 V-model · validación regulatoria',
    mockup: 'Solo CLAUDE.md y este Handoff.',
    produccion: 'Repositorio de specs en Confluence/SharePoint. Matriz de rastreabilidad URS ↔ FS ↔ DS ↔ test cases ↔ evidencia (IQ/OQ/PQ).',
  },
  {
    titulo: 'Significado de firma no separado del campo razón (resuelto en mockup, mantener en producción)',
    norma: 'GAMP 5 — ALCOA+ "Attributable"',
    mockup: 'AuditEntry.significadoFirma persiste como campo propio (FIX C1 GAMP 5).',
    produccion: 'Mantener separación. Reportes de QA pueden filtrar por significado (ej. "todas las operaciones APROBADAS por X usuario").',
  },
];

/**
 * Mapa canónico de los endpoints del mock — el backend implementa la misma
 * surface respetando los schemas zod en `src/api/contracts/`. Las "notas"
 * destacan reglas no obvias que hay que mantener server-side.
 */
const ENDPOINT_MAP: Array<{ endpoint: string; metodo: string; nota: string }> = [
  // Auth
  { endpoint: 'auth.login', metodo: 'POST /auth/login', nota: 'Devolver user + sesión + GLNs operables. Lockout tras N intentos (deuda).' },
  { endpoint: 'auth.loginConGoogle (paquete AB)', metodo: 'POST /auth/google', nota: 'Verificar idToken con google-auth-library. Validar hd=everexpharma.com. Buscar Usuario por email (sin auto-provision). Devolver JWT propio (no Google ID token).' },
  { endpoint: 'auth.reauth', metodo: 'POST /auth/reauth', nota: 'Step-up para switch GLN + ops GxP críticas. Requerido por 21 CFR Part 11 §11.10(d). MANTENER password local incluso si login fue por Google (two distinct components).' },
  { endpoint: 'auth.logout', metodo: 'POST /auth/logout', nota: 'Revoke JWT en server. Cliente limpia cache + redirect.' },
  // Catálogos
  { endpoint: 'ubicaciones.get', metodo: 'GET /catalogos/ubicaciones', nota: 'Incluir flag externa:true para laboratorios/droguerías externas (no operables).' },
  { endpoint: 'catalogos.productos', metodo: 'GET /catalogos/productos', nota: 'GTIN-14 con check digit válido (validar server-side).' },
  { endpoint: 'catalogos.clientes', metodo: 'GET /catalogos/clientes', nota: 'Cliente con domiciliosEntrega[]; cada domicilio tiene su propio GLN y tipoActor.' },
  { endpoint: 'eventos-anmat.get', metodo: 'GET /catalogos/eventos-anmat', nota: '524 entradas del catálogo SNT. Backend cachea + entrega como blob.' },
  { endpoint: 'eventos-default (paquete O)', metodo: 'GET/PATCH /config/eventos-anmat-defaults', nota: 'Custom override por tupla (informador, origen, destino). Audit cada cambio.' },
  // Series + remitos
  { endpoint: 'series.get', metodo: 'GET /series?ubicacion&estado&page', nota: 'Paginar + filtrar server-side. Indexar por (ubicacionActualId, tenencia).' },
  { endpoint: 'remitos.get', metodo: 'GET /remitos?origen&estado&page', nota: 'Items[] con seriesIds opcional (v9 — trazado directo si vienen pre-asignadas).' },
  // Operaciones core
  { endpoint: 'operaciones.get', metodo: 'GET /operaciones?desde&hasta&estado&q&page', nota: 'Filtros server-side. Index por (fecha DESC, estado, ubicacion).' },
  { endpoint: 'movimientos.get', metodo: 'GET /movimientos?serieId', nota: 'Para timeline de Trazabilidad. Sin paginación, suele ser <50 por serie.' },
  // Despacho
  { endpoint: 'despacho.confirmar', metodo: 'POST /operaciones/despacho', nota: 'TX atómica: crear Op + actualizar series.tenencia=en_transito + crear movimientos + push a queue SNT.' },
  { endpoint: 'despacho.cancelar', metodo: 'POST /operaciones/:id/cancelar', nota: 'Revertir series a en_poder + cancelar al SNT. Bloquea si confirmada (lock 🔒).' },
  // Recepción
  { endpoint: 'recepcion.confirmar', metodo: 'POST /operaciones/recepcion', nota: 'Acepta modo directa | verificar_scan (Paquete M). Atómico. Propagación CR-4 al despacho origen → confirmada.' },
  { endpoint: 'recepcion.cancelar', metodo: 'POST /operaciones/:id/cancelar-recepcion', nota: 'PAQUETE N: si estaba confirmada, revertir despacho origen → informada (atómico). Bloquear si SERIES_HAVE_LATER_MOVES.' },
  // Cola ANMAT
  { endpoint: 'anmat-ops.reintentar', metodo: 'POST /operaciones/:id/reintentar', nota: 'Re-encolar al SNT con retry policy 3×5min. Estado vuelve a en_cola.' },
  { endpoint: 'anmat-ops.editar', metodo: 'PATCH /operaciones/:id', nota: 'Solo para estado error. Diff de seriesIds: revertir tenencia de quitadas + tomar de agregadas en 1 tx. Razón obligatoria.' },
  // Sync
  { endpoint: 'sync.erp', metodo: 'POST /sync/erp', nota: 'Pull de tareas pendientes + recepciones. Idempotente. RBAC: validar ubicación.' },
  { endpoint: 'sync.buscar-remito', metodo: 'GET /sync/snt/remitos/:numero', nota: 'Fallback manual cuando el sync no trajo el remito (Paquete H). Validar GLN destino.' },
  // Ingreso (v2)
  { endpoint: 'ingreso.crear (v2)', metodo: 'POST /series/ingreso-manual', nota: 'V2 — NO IMPLEMENTAR EN V1. Crea N series con altaManual=true + ingresoId. UI funciona contra mock para validar UX.' },
  { endpoint: 'ingreso.eliminar (v2)', metodo: 'DELETE /series/ingreso/:id', nota: 'V2. Bloquear si alguna serie tiene movimientos posteriores.' },
];

/**
 * Integración con Plataforma ACSIS — endpoints, mapping y pendientes
 * (paquete AK). Fuente: PDF oficial + mails de Daniel Coturel.
 * Doc completa en docs/integraciones/acsis-plataforma.md
 */
const ACSIS_ENDPOINTS: Array<{ path: string; metodo: string; uso: string }> = [
  // Maestros
  { path: '/api/requests/buscar/log-articulos.json?desde=YYYYMMDD', metodo: 'GET', uso: 'Novedades de artículos' },
  { path: '/api/requests/obtener/articulo/:id.json', metodo: 'GET', uso: 'Detalle artículo (GTIN en "código de barras")' },
  { path: '/api/requests/buscar/log-clientes.json?desde=YYYYMMDD', metodo: 'GET', uso: 'Novedades de clientes' },
  { path: '/api/requests/obtener/cliente/:id.json', metodo: 'GET', uso: 'Cliente con domicilios[]' },
  { path: '/api/requests/buscar/log-proveedores.json?desde=YYYYMMDD', metodo: 'GET', uso: 'Novedades de proveedores' },
  { path: '/api/requests/obtener/proveedor/:id.json', metodo: 'GET', uso: 'Detalle proveedor' },
  { path: '/api/requests/buscar/proveedores.json?cuit=X', metodo: 'GET', uso: 'Buscar por CUIT (LIKE %X%)' },
  { path: '/api/requests/buscar/depositos.json', metodo: 'GET', uso: 'Lista de depósitos' },
  // Gestión colaborativa (core)
  { path: '/api/requests/buscar/tareas.json?tipo=TAE&solopendientes=1', metodo: 'GET', uso: 'Polling principal — tareas TAE pendientes' },
  { path: '/api/requests/obtener/movimiento-de-stock/:ref.json', metodo: 'GET', uso: 'Remito de venta (RT) — y destrucciones (claseComprobante 110)' },
  { path: '/api/requests/obtener/transferencia-de-stock-salida/:ref.json', metodo: 'GET', uso: 'Transferencia (TRE/TRS)' },
  { path: '/api/requests/editar/cumplir-tarea', metodo: 'POST', uso: 'Cierre del flujo: conforme 1=trazada / 0=cancelada' },
  { path: '/api/requests/chequearimac', metodo: 'GET', uso: 'Verificación post-write (sin spec detallada)' },
];

const ACSIS_MAPPING: Array<{ necesitamos: string; vive: string }> = [
  { necesitamos: 'GTIN del producto', vive: 'Atributo "código de barras" del artículo (NO es el sku directo)' },
  { necesitamos: '¿Artículo es trazable ANMAT?', vive: 'clasificador5 del payload del artículo' },
  { necesitamos: 'Serie / Lote / Vencimiento del remito', vive: 'En partidas[]: numeroDeSerie / partidaOrigen / fechaDeVencimiento (NO en items[])' },
  { necesitamos: 'Tipo de actor SNT del cliente', vive: 'clasificador2 del cliente (a configurar valores)' },
  { necesitamos: 'GLN del cliente', vive: 'domicilios[].barrio — 13 dígitos numéricos sin prefijo' },
  { necesitamos: 'GLN propio (Everex / Pharmatrans)', vive: 'Atributo "Lugar de recepción de compras" del depósito' },
  { necesitamos: 'Número AFIP del remito (R-PPVV-NNNNNNNN)', vive: 'Armar: "R-" + sucursalImpositiva.padStart(4,"0") + "-" + numero.padStart(8,"0")' },
  { necesitamos: 'claseComprobante de tareas TAE', vive: '75 = remito de venta · 110 = destrucción · 112 = transferencia' },
  { necesitamos: 'codUsuario para cumplir-tarea', vive: '"RIGA-TRAZA" (usuario fijo creado por ACSIS)' },
  { necesitamos: 'clasificadorComprobantes1 del remito', vive: 'NO se usa — los remitos a trazar vienen por /tareas.json' },
];

const ACSIS_CONTACTOS: Array<{ tema: string; nombre: string; email: string }> = [
  // ACSIS
  { tema: 'Spec API + decisiones funcionales', nombre: 'Daniel Coturel (ACSIS)', email: 'dcoturel@acsistec.com.ar' },
  { tema: 'Soporte ACSIS', nombre: 'Mariano Alonso', email: 'malonso@acsistec.com.ar' },
  { tema: 'Instalación módulos web', nombre: 'Lautaro Avalo', email: 'lavalo@acsistec.com.ar' },
  // Infra
  { tema: 'Servidor Plataforma on-prem Everex', nombre: 'Guido Savastano (Pixoit)', email: 'guido.savastano@pixoit.com.ar' },
  { tema: 'HAProxy + ZeroTier + whitelist DO', nombre: 'Gabriel Jolodovsky (ATDT)', email: 'gabriel@atdt.com.ar' },
  { tema: 'Tickets ATDT (Zendesk)', nombre: 'Soporte ATDT', email: 'soporte@atdt.com.ar' },
  // Equipo
  { tema: 'Backend dev del proyecto', nombre: 'Ezequiel Lopez Vargas', email: 'ezequiel.lopez@drofar.com.ar' },
  { tema: 'Backend dev del proyecto', nombre: 'Diego de la Parra', email: 'diego.delaparra@drofar.com.ar' },
  { tema: 'Product owner + funcional', nombre: 'Noelia Massone (Everex)', email: 'noelia.massone@everexpharma.com' },
  { tema: 'Referente integraciones', nombre: 'Christian Palazzo (Everex)', email: 'christian.palazzo@everexpharma.com' },
];

const ACSIS_PENDIENTES: string[] = [
  // Pendientes funcionales con Daniel Coturel (ACSIS)
  'Response real de un remito de medicamento trazable con partidas[] pobladas (Birat/Lidomid). Pedido sin respuesta.',
  'Response real de /obtener/articulo para confirmar dónde vive exactamente el código de barras (GTIN).',
  'Response real de cliente farmacia/droguería con clasificador2 cargado.',
  'Configurar valores de clasificador2 con la lista ANMAT (FARMACIA, DROGUERIA, HOSPITAL, etc.).',
  'Confirmar nombre exacto del atributo "Lugar de recepción de compras" en el JSON del response.',
  'Configurar tipo de movimiento de destrucción en Plataforma (imprime remito + genera TAE + asoc. cliente/proveedor).',
  'Frecuencia de polling recomendada para /buscar/tareas.json (¿30s? ¿1min? ¿5min?).',
  'TTL del token (¿permanente? ¿rota?). Política de error si vence.',
  'Lista completa de claseComprobante que pueden disparar TAE (devolución, etc.).',
  'Política de errores HTTP de Plataforma (5xx, timeout, token vencido).',
  // Pendientes de infra con Gabriel (ATDT)
  'Habilitar whitelist en https://api-erp.everexpharma.com (PROD) — armada pero sin acceso aún.',
  'Habilitar acceso del droplet bdk-prod-traza al DEV primero (hoy solo bdk-qa-1 entra).',
  'Token de producción (a tramitar con ACSIS al go-live).',
];

export function HandoffPage() {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="bg-gradient-to-r from-everex-azul to-everex-azul-bg-3 text-white">
          <CardTitle className="text-white">Handoff técnico</CardTitle>
          <p className="mt-1 text-xs text-blue-100">
            Spec viva para el equipo de desarrollo · Integración ERP Plataforma (ACSIS) + SNT (ANMAT)
          </p>
        </CardHeader>
      </Card>

      {/* =========================================================================
          KICKOFF — resumen ejecutivo para el equipo de backend
          ========================================================================= */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-everex-azul">
            🚀 Kickoff para el equipo de backend
            <span className="rounded bg-everex-naranja px-2 py-0.5 text-[11px] font-bold text-white">
              Empezar aquí
            </span>
          </CardTitle>
          <p className="text-xs text-gray-600">
            Resumen ejecutivo del handoff. Si tenés 5 minutos antes de la reunión de
            arranque, leé esta sección. El resto del documento profundiza cada punto.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* ¿Qué es esto? */}
          <div>
            <div className="mb-1 text-sm font-semibold text-everex-azul">
              ¿Qué reciben?
            </div>
            <ul className="ml-4 list-disc space-y-1 text-xs text-gray-700">
              <li>
                Front React + TypeScript estricto, listo para conectar con backend real.
                Hoy corre contra <b>mock in-memory</b> (in <code className="font-mono">src/api/mock/</code>)
                con la lógica de negocio implementada — usar como <b>spec ejecutable</b>.
              </li>
              <li>
                <b>Contratos zod</b> en <code className="font-mono">src/api/contracts/</code>{' '}
                tipifican request/response de cada endpoint. Son la <b>source of truth</b>.
              </li>
              <li>
                <b>148 unit tests</b> + <b>31 E2E</b> en verde. Las decisiones de dominio
                (RBAC, audit hash chain, GS1, filtros, política de sesión) están testeadas
                y documentadas.
              </li>
              <li>
                <b>14 deudas regulatorias</b> conocidas (sección "Pendientes" abajo). El back
                tiene que cerrarlas para arrancar OQ/PQ contra ANMAT.
              </li>
            </ul>
          </div>

          {/* Orden de implementación sugerido */}
          <div>
            <div className="mb-1 text-sm font-semibold text-everex-azul">
              Orden de implementación sugerido (priorización pragmática)
            </div>
            <ol className="ml-4 list-decimal space-y-1 text-xs text-gray-700">
              <li>
                <b>Auth + sesión + RBAC server-side</b> (JWT con TTL + Redis para revocation).
                Sin esto nada más es válido GxP.
              </li>
              <li>
                <b>Audit trail con HMAC-SHA256</b> append-only en DB + signature server-side.
                Reemplaza el hash chain FNV-1a del front (deuda crítica regulatoria).
              </li>
              <li>
                <b>Endpoints CRUD/queries</b> sobre las entidades core: ubicaciones, productos,
                clientes, series, remitos, operaciones, movimientos, eventos ANMAT. Filtros +
                paginación server-side desde el día 1.
              </li>
              <li>
                <b>Mutations de despacho/recepción</b> con transacciones atómicas. Respetá la
                propagación CR-4 + Paquete N (ver sección "Features implementadas" abajo).
              </li>
              <li>
                <b>Integración SNT real</b> con queue async (Bull/RabbitMQ). Retry policy 3×5min
                + DLQ. El mock simula con <code className="font-mono">setTimeout(2s)</code> pero
                el back necesita queue serio.
              </li>
              <li>
                <b>Integración ERP Plataforma (ACSIS)</b> para listar tareas + cerrar tareas
                post-despacho.
              </li>
              <li>
                <b>Notificaciones server-side (SSE / WebSocket)</b> — hoy es cliente-local; v2.
              </li>
              <li>
                <b>Features v2</b>: Ingreso / Lotes (alta manual de series). Marcados con badge
                "V2" en sidebar y bloqueados a Admin desde Paquete L.1. La UI funciona contra
                mock pero el back NO se implementa en v1 — la carga inicial de series va vía
                script de migración directo a DB con audit externo.
              </li>
            </ol>
          </div>

          {/* No-negociable — qué DEBE hacer el back */}
          <div>
            <div className="mb-1 text-sm font-semibold text-everex-azul">
              No-negociable (top 10 reglas del backend)
            </div>
            <div className="grid grid-cols-1 gap-1.5 md:grid-cols-2">
              {[
                'Jamás confiar en `usuarioId` del cliente — extraer del JWT validado',
                'Re-validar RBAC + ubicación en CADA mutation server-side',
                'Timestamps autoritativos del server, no del cliente',
                'Audit hash chain HMAC-SHA256 server-side, append-only, irrevocable',
                'Timeout de sesión enforced server-side (JWT exp + Redis TTL)',
                'Transacciones atómicas en despacho/recepción/cancelación',
                'Propagación CR-4 + Paquete N en cancelación de recepción',
                'Queue async + DLQ para llamadas al SNT (no setTimeout)',
                'Validar GTIN-14 / CUIT / GLN server-side (front valida UX, back enforces)',
                'Security headers: CSP, HSTS, SameSite=Strict, cookies httpOnly (no localStorage)',
              ].map((item, i) => (
                <div
                  key={i}
                  className="flex items-start gap-1.5 rounded border border-red-100 bg-red-50 p-2 text-[11px] text-red-900"
                >
                  <span className="font-bold">{i + 1}.</span>
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Endpoint surface map */}
          <div>
            <div className="mb-1 text-sm font-semibold text-everex-azul">
              Endpoint surface (canonical map del mock)
            </div>
            <div className="overflow-x-auto rounded border border-gray-100">
              <table className="w-full text-[11px]">
                <thead className="bg-gray-50 text-[11px] uppercase text-gray-600">
                  <tr>
                    <th className="px-2 py-1 text-left">Endpoint mock</th>
                    <th className="px-2 py-1 text-left">Método sugerido</th>
                    <th className="px-2 py-1 text-left">Nota crítica</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700">
                  {ENDPOINT_MAP.map((e, i) => (
                    <tr key={i} className="border-t border-gray-100">
                      <td className="px-2 py-1 font-mono text-[11px]">{e.endpoint}</td>
                      <td className="px-2 py-1 font-mono text-[11px]">{e.metodo}</td>
                      <td className="px-2 py-1">{e.nota}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Cómo correr el front en local */}
          <div>
            <div className="mb-1 text-sm font-semibold text-everex-azul">
              Setup local del front
            </div>
            <pre className="overflow-x-auto rounded bg-gray-900 p-3 text-[11px] text-gray-100">
              {`# Clonar + instalar
git clone <repo> && cd everex-traza-front
npm install

# Dev server (hot reload, mock backend en memoria)
npm run dev                    # http://localhost:5173

# Verificaciones
npm run typecheck              # tsc -b --noEmit
npm run lint                   # ESLint --max-warnings=0
npm test -- --run              # Vitest unit (148 tests)
cd e2e && npm test             # Playwright E2E (31 tests)

# Build de producción
npm run build                  # dist/ con chunks lazy (xlsx, jsPDF, bwip-js)

# Credenciales del seed
# Admin:     christian / demo  (Everex Pharma)
# Operador:  laura / demo      (Pharmatrans)
# Multi-GLN: pablo / demo      (default Pharmatrans + reauth a Everex)`}
            </pre>
          </div>

          {/* Cuándo flippar el switch al backend real */}
          <div>
            <div className="mb-1 text-sm font-semibold text-everex-azul">
              ¿Cómo apagar el mock y apuntar al back real?
            </div>
            <p className="text-xs text-gray-700">
              Los hooks de TanStack Query en{' '}
              <code className="font-mono">src/api/queries.ts</code> consumen funciones de{' '}
              <code className="font-mono">src/api/endpoints/*</code>. Hoy esas funciones son
              mock; en producción se reemplazan por fetch al backend real conservando la misma
              firma (request/response zod-validated). El cambio es <b>localizado a 1 archivo
              por endpoint</b> — no toca componentes, no toca queries.
            </p>
            <p className="mt-1 text-xs text-gray-700">
              Estrategia recomendada: implementar 1 endpoint real a la vez. El resto sigue
              contra mock, integración progresiva sin downtime de UX.
            </p>
          </div>

          {/* Contactos / dónde mirar */}
          <div>
            <div className="mb-1 text-sm font-semibold text-everex-azul">
              Si tenés dudas, mirá primero...
            </div>
            <ul className="ml-4 list-disc space-y-0.5 text-xs text-gray-700">
              <li>
                Una entidad/contrato? → <code className="font-mono">src/api/contracts/&lt;entidad&gt;.ts</code>
              </li>
              <li>
                La lógica esperada de un endpoint? → <code className="font-mono">src/api/endpoints/&lt;modulo&gt;.ts</code>{' '}
                (el mock es la spec)
              </li>
              <li>
                Reglas de negocio del dominio? → <code className="font-mono">src/domain/{'{'}rbac, anmat, gs1, traza, audit{'}'}/</code>{' '}
                (con tests al lado)
              </li>
              <li>
                Compliance regulatorio? → sección "Pendientes para producción" abajo
              </li>
              <li>
                Audit trail mantenedor → <code className="font-mono">src/stores/audit.ts</code>{' '}
                + <code className="font-mono">src/domain/audit/</code> (hash chain)
              </li>
            </ul>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-everex-azul">Stack del front React</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <DataRow label="Build / dev">Vite 5 + React 18 + TypeScript estricto</DataRow>
            <DataRow label="Styling">Tailwind 3 + shadcn/ui (Radix UI)</DataRow>
            <DataRow label="Routing">React Router 7</DataRow>
            <DataRow label="State global">Zustand 5 con persist a localStorage</DataRow>
            <DataRow label="Data fetching">TanStack Query 5 (cache + invalidation)</DataRow>
            <DataRow label="Forms + validación">react-hook-form + zod</DataRow>
            <DataRow label="Mock backend">In-memory db + endpoints async tipados</DataRow>
            <DataRow label="Tests">Vitest (unit) + Playwright (E2E)</DataRow>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-everex-azul">Contratos del API (source of truth)</CardTitle>
        </CardHeader>
        <CardContent className="text-xs">
          <p className="mb-2">
            Todos los endpoints están definidos como schemas zod en{' '}
            <code className="font-mono">src/api/contracts/</code>:
          </p>
          <ul className="list-disc space-y-1 pl-5">
            <li>
              <code className="font-mono">auth.ts</code> — login, reautenticación, logout
            </li>
            <li>
              <code className="font-mono">ubicaciones.ts, usuarios.ts, productos.ts, clientes.ts</code> — catálogos
            </li>
            <li>
              <code className="font-mono">series.ts</code> — series con tenencia + alta manual
            </li>
            <li>
              <code className="font-mono">remitos.ts</code> — input ERP (con seriesIds opcional para trazado directo v9)
            </li>
            <li>
              <code className="font-mono">operaciones.ts</code> — despachos + recepciones + movimientos
            </li>
            <li>
              <code className="font-mono">eventos-anmat.ts</code> — catálogo SNT (informador / origen / destino)
            </li>
          </ul>
          <Alert className="mt-3 border-blue-200 bg-blue-50">
            <AlertDescription className="text-xs text-blue-900">
              <b>Para Eze (back-end):</b> los schemas son la SPEC del API. Implementá los
              endpoints reales que cumplan con cada <code>z.infer&lt;...&gt;</code> de request y
              response. Los hooks <code>use*</code> en <code>src/api/queries.ts</code> ya están
              listos para consumir el back real — solo hay que apagar el mock cambiando
              <code className="font-mono"> client.ts</code>.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-everex-azul">
            Features implementadas — comportamientos críticos para el backend
            <span className="rounded bg-blue-100 px-2 py-0.5 text-[11px] font-bold text-blue-800">
              Spec
            </span>
          </CardTitle>
          <p className="text-xs text-gray-600">
            Resumen de las features y comportamientos que el front entrega listos. El
            backend debe respetar la lógica documentada acá al implementar los endpoints
            reales — los schemas zod de <code className="font-mono">src/api/contracts/</code>
            tipifican request/response, esta sección documenta la semántica.
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-3 text-xs md:grid-cols-2">
            <FeatureCard
              titulo="Despacho · pickeo + trazado directo (v9)"
              detalle="Pickeo serie por serie con validación contra ítems del remito. Si el ERP pre-asigna `seriesIds` en el remito, el front entra en modo 'trazado directo' (confirmación en bloque sin escaneo). El operador puede alternar entre modos."
              backend="POST /api/despacho/confirmar acepta `seriesIds`, `tipo`, `eventoAnmatId`, `remitoNumero`. Validar contra el remito del ERP. Mutación atómica: crear Operacion + actualizar tenencia de series + crear Movimientos + log API al SNT."
            />
            <FeatureCard
              titulo="Recepción · directa o verificación por escaneo (paquete M)"
              detalle="Operador elige modo `directa` (acepta todas las series del despacho) o `verificar_scan` (escanea una por una). En pickeo se detectan faltantes y divergencias (series no esperadas). Divergencias exigen razón obligatoria al firmar."
              backend="POST /api/recepcion/confirmar acepta `modo: 'directa'|'verificar_scan'`. Si pickeo: validar que las series escaneadas pertenecen al despacho origen. Auditar `recepcion.divergencia` cuando hay inesperadas."
            />
            <FeatureCard
              titulo="Cancelar recepción confirmada — propagación regresiva (paquete N)"
              detalle={`Cuando se cancela una recepción que ya estaba confirmada por el SNT, el despacho origen DEBE volver a estado "informada" (su confirmación fue revertida porque la contraparte se desdice). Equivale regulatoriamente a una devolución registrada.`}
              backend="POST /api/recepcion/cancelar debe ser ATÓMICO: marcar recepción como cancelada + revertir series a en_transito en GLN origen + actualizar despacho origen de confirmada → informada. Log API a SNT con el motivo. Bloquear si hay movimientos posteriores en las series (SERIES_HAVE_LATER_MOVES)."
            />
            <FeatureCard
              titulo="Cola ANMAT · reintentar / editar / cancelar (paquete H)"
              detalle="Vista consolidada de operaciones SNT con KPIs (en cola, informadas, errores, canceladas). Operaciones en error se pueden reintentar (re-envío al SNT con 80% éxito de mock) o editar (agregar/quitar series con razón obligatoria, revierte tenencia)."
              backend="POST /api/anmat/reintentar, POST /api/anmat/editar. Editar debe revertir tenencia de series quitadas + tomar tenencia de las agregadas, todo en 1 tx. Reintentar reagenda en queue."
            />
            <FeatureCard
              titulo="Evento ANMAT default custom por tupla (paquete O)"
              detalle="Admin puede setear desde Maestros → Eventos ANMAT un evento custom por tupla (informador, origen, destino). El frontend infiere el evento default respetando este custom; el operador puede hacer override con razón."
              backend="GET /api/config/eventos-anmat-defaults al login (cliente cachea). PATCH cuando Admin marca/quita default. Audit `evento_anmat.set_default` / `quitar_default` server-side con razón."
            />
            <FeatureCard
              titulo="Política de sesión configurable + auto-logout (paquete L)"
              detalle="Admin configura timeout de inactividad (5-120 min, default 30) + aviso previo (1+ min). El front escucha eventos de input con throttle 5s, tick cada 30s. Al expirar: audit `sesion.timeout` + logout + redirect a /login. GAMP 5 §11.10(d)."
              backend="ENFORCED server-side, no solo cliente. JWT exp = timeout. Cliente recibe `expiresAt` y muestra warning local. Backend rechaza requests post-timeout con 401. Política configurable vía /api/config/politica-sesion."
            />
            <FeatureCard
              titulo="Multi-GLN con switch reauth (Fase 2f)"
              detalle="Operadores con 2+ GLNs operables pueden cambiar el GLN activo con re-autenticación (password). Audit con `significadoFirma: 'Cambio de GLN operativo confirmado con password'` + diff antes/después. GLNs externos NUNCA aparecen como operables."
              backend="POST /api/auth/reauth + PATCH /api/sesion/ubicacion. Validar que el usuario tiene la ubicación en `ubicacionesIds[]`. Externos (`externa: true`) NUNCA son operables — defensa en server."
            />
            <FeatureCard
              titulo="Auth con Google Workspace SSO (paquete AB)"
              detalle="LoginPage muestra botón 'Continuar con Google' arriba del form clásico (coexistencia). Modo MOCK si `VITE_GOOGLE_CLIENT_ID` está vacío — muestra un picker de emails @everexpharma.com para QA. Modo REAL carga GIS (Google Identity Services) on-demand. Audit trail diferencia `login` vs `login_google`. NO se cambia la firma electrónica de operaciones — sigue siendo password local (Part 11 §11.200 'two distinct components')."
              backend={`POST /auth/google con body { idToken }. Verificar con google-auth-library: const ticket = await client.verifyIdToken({ idToken, audience: GOOGLE_CLIENT_ID }). Validar payload.hd === 'everexpharma.com'. Buscar Usuario por email (case-insensitive); si no existe → 403 sin auto-provision. Generar JWT propio (no devolver el Google ID token al cliente). Configurar consent screen y autorizar origin en Google Cloud Console.`}
            />
            <FeatureCard
              titulo="Audit Trail GAMP 5 · hash chain + verificación (Fase 1c)"
              detalle="Cada entrada incluye snapshot del usuario, sesión, hash de la entry previa y propia. `verificarIntegridadAuditTrail` recorre la cadena y reporta entradas rotas. Banner global visible si hay tampering."
              backend={`Hash chain HMAC-SHA256 server-side con clave privada (NO el FNV-1a 32-bit del cliente — eso es solo "tamper detection" client-side). Persistir en DB append-only. Verificar integridad en exports. 10 años de retención mínima.`}
            />
            <FeatureCard
              titulo="Filtros + paginación + export Excel en completadas (paquetes Q/R)"
              detalle="Cada listado de completadas (despacho + recepción) tiene búsqueda libre + rango fecha + estado ANMAT + tipo + contraparte. Paginación 'Cargar más' (50/página). Export Excel Admin-only. Botón PDF de trazabilidad por fila."
              backend="GET /api/operaciones?desde=ISO&hasta=ISO&estado=X&tipo=Y&contraparte=Z&q=texto&page=N&pageSize=50. Index DB por (fecha, estado, ubicacion). Export server-side opcional para queries pesadas (signed S3 URL)."
            />
            <FeatureCard
              titulo="Pantalla de detalle de operación (paquete R)"
              detalle="Ruta /operaciones/:opId con drill-down completo: datos remito + series + mensajes ANMAT + log API filtrado + observaciones del audit. URL shareable para QA."
              backend="GET /api/operaciones/:opId devuelve la operación + remito asociado + items + serieIds completos + mensajes ANMAT + log API relacionado. Permite filtrar audit por entidadId."
            />
            <FeatureCard
              titulo="DataMatrix GS1 + PDF trazabilidad (paquete J)"
              detalle="Modal de etiquetas GS1 (bwip-js) tras crear ingreso o desde Lotes. PDF descargable A4 4×6 etiquetas/página. PDF de trazabilidad por operación con header, datos, series, mensajes."
              backend="Generación cliente-side OK para v1. v2 puede mover a backend con signed URL si se requiere uniformidad de formato corporativo."
            />
            <FeatureCard
              titulo="Notificaciones in-app + sincronización ERP/SNT (paquetes K, H)"
              detalle="Bell con badge de no leídas. Botón 'Sincronizar' en header que pega a /erp/tareas + /snt/no-confirmadas + push notif si llegan novedades. 'Buscar remito en SNT' como fallback manual."
              backend="v2: notificaciones server-side (WebSocket / SSE) en lugar de push manual desde mutations. Sincronizar = GET ERP + GET SNT en paralelo, con RBAC defensivo del GLN."
            />
          </div>
        </CardContent>
      </Card>

      {/* Integración con Plataforma ACSIS — sección crítica para el backend
          (paquete AK). Toda la info viene del PDF oficial + mails de Daniel
          Coturel consolidados en docs/integraciones/acsis-plataforma.md. */}
      <Card className="border-amber-300 bg-amber-50/40">
        <CardHeader>
          <CardTitle className="flex flex-wrap items-center gap-2 text-everex-azul">
            Integración con Plataforma ACSIS (ERP)
            <span className="rounded bg-amber-200 px-2 py-0.5 text-[11px] font-bold text-amber-900">
              Crítica para el backend
            </span>
          </CardTitle>
          <p className="text-xs text-gray-600">
            Spec consolidado del ERP de ACSIS — la fuente de los remitos a
            trazar. Combina el PDF oficial de Daniel Coturel + respuestas por
            mail a consultas concretas (May/2026). Doc completa con flujo,
            pendientes y contactos en{' '}
            <code className="rounded bg-gray-200 px-1 font-mono text-[11px]">
              docs/integraciones/acsis-plataforma.md
            </code>
            .
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* URLs públicas (resueltas por ATDT el 12-may-2026) */}
          <div className="rounded border border-amber-200 bg-white p-3">
            <div className="mb-2 flex items-center gap-2 text-xs font-bold text-everex-azul">
              URLs públicas (ya armadas por ATDT)
              <span className="rounded bg-green-200 px-1.5 py-0.5 text-[10px] font-bold text-green-900">
                ✓ Activas
              </span>
            </div>
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-[11px] uppercase text-gray-500">
                  <th className="py-1 pr-2">Entorno</th>
                  <th className="py-1 pr-2">URL pública</th>
                  <th className="py-1 pr-2">Backend interno</th>
                  <th className="py-1">Estado</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-gray-100">
                  <td className="py-1.5 pr-2 font-semibold">DEV</td>
                  <td className="py-1.5 pr-2 font-mono text-[11px]">
                    https://dev-api-erp.everexpharma.com
                  </td>
                  <td className="py-1.5 pr-2 font-mono text-[11px]">
                    10.100.25.18:9094
                  </td>
                  <td className="py-1.5 text-[11px] text-green-800">
                    ✓ Habilitado para <code className="font-mono">bdk-qa-1</code> (143.198.123.24)
                  </td>
                </tr>
                <tr className="border-t border-gray-100">
                  <td className="py-1.5 pr-2 font-semibold">PROD</td>
                  <td className="py-1.5 pr-2 font-mono text-[11px]">
                    https://api-erp.everexpharma.com
                  </td>
                  <td className="py-1.5 pr-2 font-mono text-[11px]">
                    10.100.25.18:9093
                  </td>
                  <td className="py-1.5 text-[11px] text-amber-700">
                    ⚠ Armado sin whitelist · token a tramitar
                  </td>
                </tr>
              </tbody>
            </table>
            <div className="mt-2 rounded bg-blue-50 px-2 py-1.5 text-[11px] text-blue-900">
              <b>Arquitectura</b>: el backend ACSIS corre en{' '}
              <code className="font-mono">prod-02-erpiis.everex.local</code>{' '}
              (red interna Everex, IP <code className="font-mono">10.100.25.18</code>).
              ATDT armó un <b>HAProxy en <code className="font-mono">it-admin-01</code></b>{' '}
              (en DO) con túnel <b>ZeroTier</b> hacia la red interna. Las
              URLs públicas <code className="font-mono">*.everexpharma.com</code>{' '}
              apuntan al HAProxy. Whitelist por IP origen — para habilitar más
              droplets, pedirle a Gabriel (<code className="font-mono">gabriel@atdt.com.ar</code>).
            </div>
            <div className="mt-2 text-[11px] text-gray-600">
              <b>Token DEV</b>:{' '}
              <code className="rounded bg-gray-100 px-1 font-mono">MFEwGu68f3f8G</code>{' '}
              (de Daniel Coturel · ACSIS) · <b>Auth</b>:{' '}
              <code className="rounded bg-gray-100 px-1 font-mono">?token=:TOKEN</code>{' '}
              en TODAS las requests · headers{' '}
              <code className="rounded bg-gray-100 px-1 font-mono">
                Accept: application/json
              </code>{' '}
              +{' '}
              <code className="rounded bg-gray-100 px-1 font-mono">
                Content-Type: application/json
              </code>
            </div>
            <div className="mt-2 rounded bg-gray-900 p-2 font-mono text-[11px] text-gray-100">
              {`# Probar conectividad desde un droplet whitelisteado:`}<br />
              curl -s -H &quot;Accept: application/json&quot; \<br />
              {'  '}&quot;https://dev-api-erp.everexpharma.com/api/requests/buscar/tareas.json?tipo=TAE&amp;solopendientes=1&amp;token=MFEwGu68f3f8G&quot;
            </div>
          </div>

          {/* Endpoints */}
          <div className="rounded border border-amber-200 bg-white p-3">
            <div className="mb-2 text-xs font-bold text-everex-azul">
              13 endpoints documentados
            </div>
            <table className="w-full text-[11px]">
              <thead>
                <tr className="text-left uppercase text-gray-500">
                  <th className="py-1 pr-2">Endpoint</th>
                  <th className="py-1 pr-2">Método</th>
                  <th className="py-1">Uso</th>
                </tr>
              </thead>
              <tbody className="font-mono">
                {ACSIS_ENDPOINTS.map((ep, i) => (
                  <tr key={i} className="border-t border-gray-100">
                    <td className="py-1 pr-2">{ep.path}</td>
                    <td className="py-1 pr-2 text-everex-azul">{ep.metodo}</td>
                    <td className="py-1 font-sans text-gray-700">{ep.uso}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mapping de campos críticos */}
          <div className="rounded border border-amber-200 bg-white p-3">
            <div className="mb-2 text-xs font-bold text-everex-azul">
              Mapping crítico (de mails de Daniel · NO está en el PDF)
            </div>
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-[11px] uppercase text-gray-500">
                  <th className="py-1 pr-2">Necesitamos</th>
                  <th className="py-1">Vive en</th>
                </tr>
              </thead>
              <tbody>
                {ACSIS_MAPPING.map((m, i) => (
                  <tr key={i} className="border-t border-gray-100 align-top">
                    <td className="py-1.5 pr-2 text-xs">{m.necesitamos}</td>
                    <td className="py-1.5 text-xs text-gray-700">{m.vive}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Flujo principal — 6 pasos del modelo RTO-001 (gestión colaborativa) */}
          <div className="rounded border border-amber-200 bg-white p-3">
            <div className="mb-2 text-xs font-bold text-everex-azul">
              Flujo principal — 6 pasos (modelo RTO-001 acordado)
            </div>
            <ol className="space-y-1.5 text-xs text-gray-700">
              <li>
                <b className="text-everex-azul">1.</b> Polling cada N minutos:{' '}
                <code className="rounded bg-gray-100 px-1 font-mono text-[11px]">
                  GET /buscar/tareas.json?tipo=TAE&amp;solopendientes=1
                </code>
              </li>
              <li>
                <b className="text-everex-azul">2.</b> Por cada tarea, identificar tipo
                por <code className="rounded bg-gray-100 px-1 font-mono text-[11px]">claseComprobante</code>:
                <ul className="ml-5 mt-0.5 list-disc text-[11px]">
                  <li><b>75</b> = Remito de venta (RT)</li>
                  <li><b>110</b> = Movimiento de destrucción</li>
                  <li><b>112</b> = Transferencia (TRE/TRS)</li>
                </ul>
              </li>
              <li>
                <b className="text-everex-azul">3.</b> Obtener detalle del comprobante por la{' '}
                <code className="rounded bg-gray-100 px-1 font-mono text-[11px]">referencia</code>{' '}
                (ej. <code className="font-mono">1-1-RT-25</code>):
                <ul className="ml-5 mt-0.5 list-disc text-[11px]">
                  <li>RT y destrucciones → <code className="font-mono">/obtener/movimiento-de-stock/:ref</code></li>
                  <li>TRE/TRS → <code className="font-mono">/obtener/transferencia-de-stock-salida/:ref</code></li>
                </ul>
              </li>
              <li>
                <b className="text-everex-azul">4.</b> Si aplica, obtener cliente con{' '}
                <code className="rounded bg-gray-100 px-1 font-mono text-[11px]">/obtener/cliente/:id</code>.
                Usar <code className="font-mono">clasificador2</code> para tipo de actor
                SNT, y <code className="font-mono">domicilios[].barrio</code> para el GLN.
              </li>
              <li>
                <b className="text-everex-azul">5.</b> El operador ejecuta la traza en
                Riga Traza (escanea series, confirma evento ANMAT, envía al SNT).
              </li>
              <li>
                <b className="text-everex-azul">6.</b> Cerrar la tarea en Plataforma con{' '}
                <code className="rounded bg-gray-100 px-1 font-mono text-[11px]">
                  POST /editar/cumplir-tarea
                </code>{' '}
                (ver sección siguiente).
              </li>
            </ol>
          </div>

          {/* Decisión RTO-001 — gestión colaborativa */}
          <div className="rounded border border-blue-200 bg-blue-50/60 p-3">
            <div className="mb-2 text-xs font-bold text-everex-azul">
              ¿Por qué gestiones colaborativas? Decisión RTO-001 (30-abr-2026)
            </div>
            <div className="space-y-1 text-xs text-gray-700">
              <p>
                ACSIS planteó dos modelos posibles:{' '}
                <b>RTO-001 (Plataforma sabe qué se traza)</b> o RTO-002 (el
                sistema de traza descubre los remitos solo, mirando todos los
                movimientos de stock).
              </p>
              <p>
                Acordamos <b>RTO-001</b>: Plataforma genera automáticamente
                una "gestión" (tarea TAE) sobre cada remito nuevo que requiere
                traza. La gestión queda pendiente de cumplimiento hasta que el
                front la marca como{' '}
                <code className="font-mono">conforme: 1</code> (trazada) o{' '}
                <code className="font-mono">0</code> (cancelada).
              </p>
              <p>
                <b>Ventajas operativas</b>: pull simple sin lógica de filtrado
                del lado del front · soporta agregar clasificadores que
                Plataforma no tiene hoy en los remitos · cada operación queda
                trazada en Plataforma (estado del backlog visible para el
                administrador).
              </p>
            </div>
          </div>

          {/* Cumplir tarea */}
          <div className="rounded border border-amber-200 bg-white p-3">
            <div className="mb-2 text-xs font-bold text-everex-azul">
              POST /cumplir-tarea — cierre del flujo
            </div>
            <pre className="overflow-x-auto rounded bg-gray-900 p-2 font-mono text-[11px] text-gray-100">
{`POST /api/requests/editar/cumplir-tarea?token=...
Content-Type: application/json

{
  "tareaId": 11,
  "codUsuario": "RIGA-TRAZA",   // usuario fijo creado por ACSIS
  "conforme": 1,                 // 1 = Traza realizada · 0 = cancelada
  "comentario": "Trazado OK · TX-XYZ"
}`}
            </pre>
          </div>

          {/* Contactos */}
          <div className="rounded border border-amber-200 bg-white p-3">
            <div className="mb-2 text-xs font-bold text-everex-azul">
              Contactos — a quién preguntarle qué
            </div>
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-[11px] uppercase text-gray-500">
                  <th className="py-1 pr-2">Tema</th>
                  <th className="py-1 pr-2">Referente</th>
                  <th className="py-1">Contacto</th>
                </tr>
              </thead>
              <tbody>
                {ACSIS_CONTACTOS.map((c, i) => (
                  <tr key={i} className="border-t border-gray-100 align-top">
                    <td className="py-1.5 pr-2 text-[11px]">{c.tema}</td>
                    <td className="py-1.5 pr-2 text-[11px] font-semibold">{c.nombre}</td>
                    <td className="py-1.5 font-mono text-[11px] text-gray-700">
                      {c.email}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Docs en el repo */}
          <div className="rounded border border-amber-200 bg-white p-3">
            <div className="mb-2 text-xs font-bold text-everex-azul">
              Documentos en el repo
            </div>
            <ul className="space-y-1 text-xs text-gray-700">
              <li className="flex items-start gap-2">
                <span className="font-mono">📄</span>
                <span>
                  <code className="rounded bg-gray-100 px-1 font-mono text-[11px]">
                    docs/integraciones/acsis-plataforma.md
                  </code>{' '}
                  — Doc consolidada (esta misma info en markdown, con diagrama
                  ASCII de la arquitectura y curl de prueba).
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-mono">📄</span>
                <span>
                  <code className="rounded bg-gray-100 px-1 font-mono text-[11px]">
                    docs/integraciones/acsis-api-comprobantes-v2026-05-08.pdf
                  </code>{' '}
                  — Spec oficial de Daniel Coturel (ACSIS), 18 páginas con
                  ejemplos de payload y response.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-mono">🔗</span>
                <span>
                  Sharepoint ACSIS (acceso restringido):{' '}
                  <a
                    href="https://acsistec.sharepoint.com/:w:/g/IQDV9UooqqmwTJcgSVf4VNwCAbc27BNUAA8rAiRFn80A7Ss"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-everex-azul underline"
                  >
                    versión .docx editable
                  </a>{' '}
                  — preferir esta para ver la última versión actualizada.
                </span>
              </li>
            </ul>
          </div>

          {/* Pendientes con ACSIS */}
          <div className="rounded border border-red-300 bg-red-50/60 p-3">
            <div className="mb-2 text-xs font-bold text-red-900">
              ⚠️ Pendientes con ACSIS antes de codear
            </div>
            <ul className="space-y-1 text-xs text-red-900">
              {ACSIS_PENDIENTES.map((p, i) => (
                <li key={i} className="flex items-start gap-1.5">
                  <span className="mt-0.5">▸</span>
                  <span>{p}</span>
                </li>
              ))}
            </ul>
            <div className="mt-2 text-[11px] text-red-800">
              Contacto: <b>Daniel Coturel</b> ·{' '}
              <code className="font-mono">dcoturel@acsistec.com.ar</code>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-everex-azul">
            Alcance v1 (MVP) vs v2 (post-MVP)
            <span className="rounded bg-amber-100 px-2 py-0.5 text-[11px] font-bold text-amber-800">
              Scope
            </span>
          </CardTitle>
          <p className="text-xs text-gray-600">
            Qué features deben tener backend funcional en el primer release (v1) vs cuáles
            se posponen para una iteración posterior (v2). Las features marcadas v2 funcionan
            contra el mock para validar UX y flujos, pero el equipo de backend NO debe
            priorizarlas en v1.
          </p>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Feature</TableHead>
                  <TableHead className="text-center">v1 (MVP)</TableHead>
                  <TableHead className="text-center">v2</TableHead>
                  <TableHead>Notas para backend</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ALCANCE.map((a, i) => (
                  <TableRow key={i} className="align-top">
                    <TableCell className="text-xs font-semibold text-everex-azul">
                      {a.feature}
                    </TableCell>
                    <TableCell className="text-center">
                      <AlcanceBadge estado={a.v1} />
                    </TableCell>
                    <TableCell className="text-center">
                      <AlcanceBadge estado={a.v2} />
                    </TableCell>
                    <TableCell className="text-[11px] text-gray-700">{a.notas}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <Alert className="mt-3 border-amber-200 bg-amber-50">
            <AlertDescription className="text-xs text-amber-900">
              <b>Ingreso / Alta y Lotes:</b> excluidos de v1. Para la carga inicial de
              series, el equipo de migración usa un script directo a la DB con audit
              externo. La UI queda construida y validada para que cuando llegue v2 solo
              haya que conectar los endpoints reales.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-everex-azul">
            Pendientes para producción — compliance GAMP 5 / 21 CFR Part 11
            <span className="ml-2 rounded bg-red-100 px-2 py-0.5 text-[11px] font-bold text-red-700">
              Regulatorio
            </span>
          </CardTitle>
          <p className="text-xs text-gray-600">
            14 puntos surgidos de la auditoría multi-dimensional + auditoría GAMP 5. Son
            aceptables para validación de UX, pero deben resolverse antes del arranque de
            OQ/PQ contra ANMAT.
          </p>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Punto</TableHead>
                  <TableHead>Norma</TableHead>
                  <TableHead>Estado en mockup/React</TableHead>
                  <TableHead>Solución en producción</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {DEUDA.map((d, i) => (
                  <TableRow key={i} className="align-top">
                    <TableCell className="text-xs font-semibold text-everex-azul">
                      {d.titulo}
                    </TableCell>
                    <TableCell className="text-[11px] text-gray-600">{d.norma}</TableCell>
                    <TableCell className="text-[11px] text-gray-700">{d.mockup}</TableCell>
                    <TableCell className="text-[11px] text-gray-700">{d.produccion}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <Alert className="mt-3 border-amber-200 bg-amber-50">
            <AlertDescription className="text-xs text-amber-900">
              <b>Recomendación QA:</b> incluir esta lista en el plan de validación como
              pre-requisitos para arranque de OQ/PQ. Cada ítem necesita ticket con criterios
              de aceptación y evidencia de implementación antes de la verificación regulatoria.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    </div>
  );
}

function FeatureCard({
  titulo,
  detalle,
  backend,
}: {
  titulo: string;
  detalle: string;
  backend: string;
}) {
  return (
    <div className="rounded border border-gray-200 bg-white p-3">
      <div className="font-semibold text-everex-azul">{titulo}</div>
      <div className="mt-1 text-gray-700">{detalle}</div>
      <div className="mt-2 rounded bg-blue-50 p-2 text-blue-900">
        <div className="font-semibold text-[11px] uppercase">Backend</div>
        <div className="mt-0.5">{backend}</div>
      </div>
    </div>
  );
}

function DataRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[11px] uppercase text-gray-600">{label}</span>
      <span className="text-sm">{children}</span>
    </div>
  );
}

function AlcanceBadge({ estado }: { estado: 'incluido' | 'parcial' | 'excluido' }) {
  if (estado === 'incluido') {
    return <span className="badge badge-green">✓ Incluido</span>;
  }
  if (estado === 'parcial') {
    return <span className="badge badge-yellow">~ Parcial</span>;
  }
  return <span className="badge badge-gray">✗ Excluido</span>;
}
